import Offcanvas from 'bootstrap/js/dist/offcanvas';
import { popupLinkConfig } from '../config/popup-link';
import { yieldToMain } from './helpers';

/**
 * Attach swipe-to-close behavior to a single offcanvas element.
 * This controller keeps per-element state and does not rely on globals.
 */
class SwipeOffcanvasController {
  /**
   * @param {HTMLElement} el - The .offcanvas root element
   */
  constructor(el) {
    this.el = el;
    this.sliderBtn = el.querySelector('.offcanvas__slider');
    this.body = el.querySelector('.offcanvas-body');

    // State
    this.touching = false;
    this.isOpen = false;
    this.isMoving = false;
    this.dragDirection = ''; // 'vertical' | 'horizontal' | ''
    this.height = 0;

    // Pointers
    this.startTime = 0;
    this.startX = 0;
    this.startY = 0;
    this.currX = 0;
    this.currY = 0;
    this.lastX = 0;
    this.lastY = 0;

    // Translation state
    this.moveY = 0;

    // Bootstrap instance (lazy)
    this.instance = Offcanvas.getOrCreateInstance(this.el);

    // Bind methods
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._onTransitionEnd = this._onTransitionEnd.bind(this);
    this._rafUpdate = this._rafUpdate.bind(this);

    this._addEvents();
  }

  // —————————————————————————————————————————————————————
  // Event binding
  _addEvents() {
    // touchstart can be passive; touchmove must be non-passive if we call preventDefault.
    this.el.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this.el.addEventListener('touchmove', this._onTouchMove, { passive: false });
    this.el.addEventListener('touchend', this._onTouchEnd, { passive: true });
  }

  destroy() {
    this.el.removeEventListener('touchstart', this._onTouchStart);
    this.el.removeEventListener('touchmove', this._onTouchMove);
    this.el.removeEventListener('touchend', this._onTouchEnd);
  }

  // —————————————————————————————————————————————————————
  // Touch handlers
  _onTouchStart(e) {
    // Guard: require a valid body, otherwise we might block scroll unexpectedly
    const bodyScrollTop = this.body ? this.body.scrollTop : 0;
    const isOnSlider = this.sliderBtn && this.sliderBtn.contains(e.target);

    // Only engage gesture if:
    // 1) user starts from top of offcanvas body (pull-down to close), or
    // 2) user starts on the slider handle button (explicit)
    if (!(bodyScrollTop <= 0 || isOnSlider)) return;

    const t = e.touches[0];
    this.startTime = Date.now();
    this.startY = t.pageY;
    this.startX = t.pageX;
    this.currX = 0;
    this.currY = 0;
    this.lastX = this.startX;
    this.lastY = this.startY;

    this.isOpen = this.el.classList.contains('show');
    this.height = this.el.offsetHeight;

    // Disable CSS transition during drag to keep it buttery smooth
    this.el.classList.add('offcanvas--no-transition');

    // Initialize translation position depending on current state
    this.moveY = this.isOpen ? 0 : -this.height;

    this.dragDirection = '';
    this.touching = true;
    this.isMoving = true;

    // Kick the RAF loop
    requestAnimationFrame(this._rafUpdate);
  }

  _onTouchMove(e) {
    if (!this.touching) return;

    const t = e.touches[0];
    this.currY = t.pageY;
    this.currX = t.pageX;

    const translateY = this.currY - this.startY;
    const translateX = this.currX - this.startX;

    // Determine drag axis once
    if (!this.dragDirection) {
      this.dragDirection = Math.abs(translateY) >= Math.abs(translateX) ? 'vertical' : 'horizontal';
    }

    if (this.dragDirection === 'horizontal') {
      // Ignore horizontal drags (do not block native scroll)
      this.lastY = this.currY;
      this.lastX = this.currX;
      return;
    }

    // Vertical drag — only intercept when moving downward within bounds
    const deltaY = this.currY - this.lastY;

    // When pulling down, we want to prevent native scroll to capture gesture
    const nextMove = this.moveY + deltaY;

    // Allow movement only within [-height, 0] to avoid over-drag
    if (nextMove <= 0) {
      // When moving further up (closing already closed), let events bubble/scroll
      // but do not block if not necessary
      this.lastY = this.currY;
      this.lastX = this.currX;
      return;
    }

    // We are dragging down: prevent page scroll and update model
    if (e.cancelable) e.preventDefault();

    // Clamp to [0, height] just in case; we only render translateY in RAF
    this.moveY = Math.min(nextMove, this.height);

    this.lastY = this.currY;
    this.lastX = this.currX;
  }

  _onTouchEnd() {
    if (!this.touching) return;

    this.touching = false;
    this.isMoving = false;

    // Compute deltas and velocity
    const totalY = this.currY - this.startY;
    const dt = Math.max(1, Date.now() - this.startTime); // ms
    const vy = Math.abs(totalY) / dt; // px per ms

    // Restore transition for the snap animation
    this.el.classList.remove('offcanvas--no-transition');

    // Decide open/close. Threshold and velocity tuned for mobile UX.
    const HALF = this.height / 2;
    const VELOCITY = 0.3; // ~0.3 px/ms

    if (this.isOpen) {
      // Offcanvas is open: downward swipe closes it
      if (totalY > HALF || vy > VELOCITY) {
        this._closeWithSnap(totalY);
        this.isOpen = false;
      } else {
        this._snapOpen();
        this.isOpen = true;
      }
    } else {
      // Offcanvas is closed (partially visible via transform): open if large pull
      if (totalY > HALF) {
        this._snapOpen();
        this.isOpen = true;
      } else {
        this._closeWithSnap(totalY);
        this.isOpen = false;
      }
    }
  }

  // —————————————————————————————————————————————————————
  // Rendering helpers
  _rafUpdate() {
    if (!this.isMoving) return;
    // Render visual translation during drag; let Bootstrap handle show/hide transitions.
    this.el.style.transform = `translateY(${this.moveY}px)`;
    this.el.style.webkitTransform = `translateY(${this.moveY}px)`;
    requestAnimationFrame(this._rafUpdate);
  }

  _resetTransform() {
    this.el.style.transform = '';
    this.el.style.webkitTransform = '';
  }

  _snapOpen() {
    // Remove drag transform and show the offcanvas (no transform)
    this._resetTransform();
    // When already open, nothing to do. If it was closed, caller will show().
    // Here we just ensure visual state is reset.
  }

  _closeWithSnap(totalY) {
    // If pulled down or currently closed, hide via Bootstrap (ensures backdrop cleanup)
    if (totalY > 0 || !this.isOpen) {
      this._resetTransform();
      // Hide will animate with transition; when done, reset inner scroll
      this.el.addEventListener('transitionend', this._onTransitionEnd, false);
      this.instance.hide();
    }
  }

  _onTransitionEnd() {
    // Reset body scroll so the next open starts from top
    if (this.body) this.body.scrollTop = 0;
    this.el.removeEventListener('transitionend', this._onTransitionEnd, false);
  }

  // Public utility for lifecycle
  forceClose() {
    this._resetTransform();
    this.el.classList.remove('offcanvas--no-transition');
    try {
      this.instance.hide();
    } catch (_) {
      // no-op
    }
    if (this.body) this.body.scrollTop = 0;
  }
}

/**
 * Manager responsible for discovering all .offcanvas and instantiating controllers.
 */
const OffcanvasManager = {
  controllers: new WeakMap(),

  initAll() {
    const nodes = document.querySelectorAll('.offcanvas');
    nodes.forEach((el) => {
      if (!this.controllers.has(el)) {
        const controller = new SwipeOffcanvasController(el);
        this.controllers.set(el, controller);
      }
    });
  },
};

// —————————————————————————————————————————————————————
// Turbo lifecycle hooks
document.addEventListener('turbo:load', async() => {
  await yieldToMain(); // allow main thread to settle (layout/paint) before attaching
  OffcanvasManager.initAll();
});

document.addEventListener('turbo:before-cache', () => {
  document.querySelectorAll('.offcanvas.show').forEach((el) => {
    Offcanvas.getInstance(el)?.hide();
  });
  // Clean stray backdrops if any remained
  const backdrops = document.querySelectorAll('.offcanvas-backdrop');
  backdrops.forEach((bd) => bd.remove());
});

// —————————————————————————————————————————————————————
// Programmatic API for content injection (kept compatible with your usage)
class OffCanvasComponent {
  /**
   * @param {string} title - HTML content for the title
   * @param {string} body - HTML content for the body
   * @param {'auto'|'top'|'bottom'|'start'|'end'} position
   * @param {string} customClass - Additional classes to add on the offcanvas root
   */
  constructor(title, body, position = 'auto', customClass = '') {
    this.title = title;
    this.body = body;
    this.customClass = customClass;
    this.position = position === 'auto' ? 'bottom' : position;
    this.elementId = `display-content-offcanvas-${this.position}`;
    this.closeButtonClass = 'offcanvas-close';

    this._injectContent();

    this.offCanvasElement = document.getElementById(this.elementId);
    this.offcanvas = Offcanvas.getOrCreateInstance(this.offCanvasElement);

    // Ensure the element is registered with the swipe manager as well
    OffcanvasManager.initAll();

    this._bindCloseHandlers();
  }

  _injectContent() {
    // NOTE: using jQuery here to keep compatibility with your existing codebase.
    // Consider migrating to vanilla if you want to remove the dependency.
    $(`#${this.elementId} .offcanvas-title`).html(this.title);
    $(`#${this.elementId} .offcanvas-body`).html(this.body);
    $(`#${this.elementId}`)
      .addClass(`offcanvas-${this.position} ${this.customClass}`);
  }

  _bindCloseHandlers() {
    const offCanvas = this.offcanvas;

    // Close via elements rendered inside the offcanvas (popupLinkConfig.closeBtnClass)
    $(document).on('click', `#${this.elementId} .${popupLinkConfig.closeBtnClass}`, function() {
      offCanvas.hide();
    });

    // Close via any explicit button rendered for desktop
    $('body').on('click', `.${this.closeButtonClass}`, function() {
      offCanvas.hide();
    });
  }

  show() {
    this.offcanvas.show();
  }
}

export { OffCanvasComponent };
